import { FACTOR_LABELS, hitsByGroup } from './planetaryAnalog';

/**
 * THE PLANETARY ANALOG SCANNER, on the chart.
 *
 * <h2>Why this exists at all: the table cannot answer the question being asked</h2>
 *
 * The scanner's measured verdict is that its analogs carry no directional edge — t = 0.65 / 0.70
 * / 0.09 at 5 / 10 / 20 days on SPY over sixty of them. That settles the QUANTITATIVE question
 * and leaves the qualitative one untouched: whether an analog tends to land near a turn, a
 * consolidation, a gap, the start of a run. A table of dates and bit scores cannot be read for
 * that. A mark on the bar, with the price action either side of it, can.
 *
 * <p>So this is an instrument for looking, and it is deliberately built not to pre-empt what is
 * looked at — see the badge note below.
 *
 * <h2>Every mark sits on the RAIL, because an analog is a DATE</h2>
 *
 * The scanner compares planetary signatures. It computes no price, no level and no direction, and
 * nothing in `AnalogMatchResult` could supply one. A mark at a price — and the only price
 * available would be one picked off the bar — would make a claim the study never makes, which is
 * exactly what the CTS triangles were moved off the candles for.
 *
 * <p>The points therefore carry NO `price` at all, and that absence IS the instruction:
 * `onRailFor` in the renderer routes a point with no finite price to the pinned 0..1 rail by
 * rule. Same convention as the eclipse study and the midpoint-trine scan.
 *
 * <h2>⚠ AND A FAINT VERTICAL, because 25 marks in 8,485 columns cannot be found</h2>
 *
 * This is the one place this overlay departs from the eclipse study's shape, and the reason is
 * density rather than taste. That study marks up to 1,719 bars on a 33-year chart, so the rail
 * reads as a band and the eye lands on it. This one marks the LIMIT — twenty-five by default —
 * and an 18px tick every few hundred columns is not findable by scanning; a reader would have to
 * hunt for the thing they came to look at.
 *
 * <p>The vertical is what lets the eye travel from the mark to the candle above it, which is the
 * whole gesture this feature exists to support. It rides the shared `markLines` array, so it is
 * forced `silent` and `symbol: 'none'` by the renderer — correct here: the hover card belongs to
 * the rail mark, and a full-height line that stole the hover would cover the price action with a
 * tooltip every time the pointer crossed it.
 *
 * <h2>⚠ NO OUTCOME BADGE, deliberately</h2>
 *
 * The forward return of each analog is known and could be drawn beside the mark. It is not, and
 * the omission is the point: the reader is being asked to form a view from the price action, and
 * printing the answer on the canvas pre-empts exactly the judgement the exercise is for. It is in
 * the hover card, which is a thing you choose to read.
 *
 * <h2>The rank is INSIDE the symbol, so the chart and the table are one object</h2>
 *
 * A reader works down the table and looks at the chart; without a shared key those are two lists
 * of dates. The digit is the table's own row number, so "row 3 is that bar" needs no matching of
 * dates by eye.
 *
 * <h2>A non-trading day has no column</h2>
 *
 * The x axis is a category axis over the `YYYY-MM-DD` strings in `barData.dates`. An analog is a
 * calendar day and the sky does not keep market hours, so roughly two in seven fall on a day the
 * axis has no column for — handed straight to the renderer those points vanish in silence. They
 * are SNAPPED FORWARD to the next trading day, as every other dated study here is, and the card
 * for that bar says so.
 */

/** One id, so a re-run replaces the previous paint rather than stacking on it. */
export const PLANETARY_ANALOG_OVERLAY_ID = 'planetary-analog-markers';

/**
 * A strong magenta-pink.
 *
 * <p>Chosen against BOTH canvases rather than by eye, because the app defaults to dark and a hue
 * picked on white routinely fails there. Composited against `#f9fafe` and `#0d1117` this measures
 * **4.17:1 on light and 4.39:1 on dark**, clearing the 3:1 a graphical object needs on each.
 *
 * <p>Pink is the one band of the wheel this chart has free. Everything else that can share it is
 * taken: Deep Celestial indigo `#4C5B8A`, CTS amber `#FFC107`, PTA `#F59E0B`, PTB `#06B6D4`, Deep
 * Pivot bronze `#8B7355`, the yearly plaque's gold `#D4A017`, midpoint-trine teal `#00796B` and
 * the eclipse study's purple `#AB47BC`. The Workbench's human-sell rose `#F43F5E` is close, and
 * cannot collide: that palette is drawn only inside a replay session, which this is not.
 */
export const PLANETARY_ANALOG_COLOR = '#E91E63';

/**
 * At most this many BARS carry a mark.
 *
 * <p>The server caps `limit` at 500 and defaults to 25, so this binds only on a deliberately wide
 * request. The eclipse study measured 3,000 scatter marks as free on this chart, so the cost is
 * not the marks — it is the VERTICALS, which are ink across the price action and stop being
 * readable as individual columns long before 500 of them.
 *
 * <p>⚠ THE SLICE IS THE HEAD, which is the opposite of the eclipse study's rule and for a reason.
 * There the marks are in bar order and the reader is looking at the right-hand end, so the most
 * RECENT are kept. Here `matches` arrives ranked by weighted score, so the head is the strongest
 * — dropping the tail drops the weakest analogs, which is what a cap should do to a ranking.
 *
 * <p>REPORTED by the caller rather than applied in silence: a truncated overlay reads as "these
 * are all of them".
 */
export const PLANETARY_ANALOG_MAX_MARKS = 200;

/** How many factors a card names before it stops. A card is read, not audited. */
export const CARD_MAX_FACTORS = 6;

/**
 * THE TYPE OF A MATCH: which family of agreement is UNUSUALLY STRONG in it.
 *
 * <h2>⚠ RELATIVE TO THE SCAN, because every absolute measure collapses — measured</h2>
 *
 * The obvious type is "the family with the most raw bits". On a live 25-analog SPY run that
 * answered **ASP for 24 of 25**. The obvious correction — bits per FACTOR, to stop a family
 * winning on size — answered **HEL for 22 of 25**. Both are constant because both are reporting
 * a property of the FACTOR SET, which is identical for every match: there are ~66 aspect pairs
 * and a handful of heliocentric ones, and heliocentric agreements are individually rarer. The
 * match has nothing to do with it.
 *
 * <p>So the type is the family whose share of THIS match's bits most exceeds its own AVERAGE
 * share across the matches in this scan. That is discriminating by construction, and it is also
 * the question a reader actually has standing in front of a chart: not "which family is biggest"
 * — always the same one — but "what is unusual about this one". The same 25 analogs typed this
 * way came out ASP 7 · HEL 8 · NAV 7 · DEC 2 · DIS 1.
 *
 * <p>A RATIO was measured too and rejected: dividing by a 1.4% baseline makes a family's code
 * a function of its own noise, and it spread the 25 across all seven codes with STA and DIS
 * appearing on the strength of rounding.
 *
 * <h2>The cost, stated rather than hidden</h2>
 *
 * The baseline is the returned set, so **changing the limit can change a code**. That is the
 * price of the only measure that discriminates, and the card prints both the match's share and
 * the run average so a reader can see exactly what the letters rest on.
 */
export const FAMILY_CODES = {
  ASPECT: 'ASP',
  HELIO_PHASE: 'HEL',
  NAVAMSHA: 'NAV',
  DECLINATION: 'DEC',
  DISTANCE: 'DIS',
  STATION: 'STA',
  RETROGRADE: 'RET',
};

/** Raw bits across every family, so a share has a denominator a reader can check. */
export function sumBits(match) {
  return Object.values(hitsByGroup(match)).reduce((s, v) => s + (v.bits || 0), 0);
}

/** Each family's share of this match's raw bits, keyed by group. */
export function familyShares(match) {
  const total = sumBits(match);
  const out = {};
  if (total <= 0) return out;
  Object.entries(hitsByGroup(match)).forEach(([g, v]) => { out[g] = (v.bits || 0) / total; });
  return out;
}

/**
 * The mean share of each family across a whole scan — the baseline a type is measured against.
 *
 * <p>Computed from EVERY match in the report, not only the ones that land on the chart: the
 * panel and the canvas must agree about a match's type, and an analog dropped for being older
 * than the price history is still one of the scan's own results.
 */
export function baselineShares(matches) {
  const list = Array.isArray(matches) ? matches : [];
  if (!list.length) return {};
  const sums = {};
  list.forEach((m) => {
    Object.entries(familyShares(m)).forEach(([g, s]) => { sums[g] = (sums[g] || 0) + s; });
  });
  const out = {};
  Object.entries(sums).forEach(([g, s]) => { out[g] = s / list.length; });
  return out;
}

/**
 * The type of one match against a baseline.
 *
 * <p>Returns `null` for a match with no hits — never a placeholder code, because "nothing
 * agreed" and "a family called NONE agreed" are different facts.
 *
 * <p>With a baseline of ONE match every lift is zero and the comparison says nothing, so it
 * falls back to the largest share and `relative` is false. The card says which it used.
 */
export function matchType(match, baseline) {
  const shares = familyShares(match);
  const groups = Object.keys(shares);
  if (!groups.length) return null;
  const base = baseline || {};
  const relative = Object.keys(base).length > 0
    && Object.values(base).some((v) => v > 0)
    // A baseline built from this match alone is this match, so the lift is identically zero.
    && groups.some((g) => Math.abs(shares[g] - (base[g] || 0)) > 1e-9);
  const score = (g) => (relative ? shares[g] - (base[g] || 0) : shares[g]);
  const group = groups.reduce((best, g) => (score(g) > score(best) ? g : best));
  const stats = hitsByGroup(match)[group] || { count: 0, bits: 0 };
  return {
    group,
    code: FAMILY_CODES[group] || group.slice(0, 3).toUpperCase(),
    label: FACTOR_LABELS[group] || group,
    count: stats.count,
    bits: stats.bits,
    share: shares[group],
    baseline: base[group] || 0,
    lift: shares[group] - (base[group] || 0),
    relative,
  };
}

const asPercent = (v) => `${Math.round((v || 0) * 100)}%`;

/**
 * ONE LINE saying what type this is, for the hover card.
 *
 * <p>Declared here rather than inside the card builder because the panel's Type column reads the
 * same `matchType`, and a second derivation is how a chart and a table come to label one match
 * two different ways.
 */
export function typeLine(match, baseline, scanSize) {
  const t = matchType(match, baseline);
  if (!t) return 'Type: none — no factor agreed on this day.';
  if (!t.relative) {
    return `Type: ${t.code} — ${t.label} lead, ${asPercent(t.share)} of this match's raw bits`
      + ' (no other analog to compare against)';
  }
  return `Type: ${t.code} — ${t.label} stand out`
    + ` (${asPercent(t.share)} of this match's raw bits against ${asPercent(t.baseline)}`
    + `${scanSize ? ` across the ${scanSize} analogs in this scan` : ' across this scan'})`;
}

/** The vertical's weight. Low enough to pass behind the candles, visible enough to follow. */
export const VERTICAL_OPACITY = 0.3;

/**
 * The index of the first trading day on or after a date, or -1 past the end.
 *
 * <p>`dates` is sorted and in `YYYY-MM-DD`, so a string comparison IS a date comparison. Parsing
 * would reintroduce the timezone shift this repository has already been caught by, to answer a
 * question that needs no clock.
 */
export function indexAtOrAfter(dates, day) {
  if (!Array.isArray(dates) || dates.length === 0 || !day) return -1;
  let lo = 0;
  let hi = dates.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] >= day) {
      found = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return found;
}

/**
 * Calendar days from `a` to `b`, or NaN.
 *
 * <p>⚠ `Date.UTC` FROM THE PARTS, never `new Date(iso)`. The local-time constructor shifts a date
 * across a day boundary for any reader west of UTC, so two people looking at the same chart would
 * be told different gaps. This repository has already been bitten by exactly that in the CTS
 * dedup fold and in the exit-date mapping.
 */
export function daysBetween(a, b) {
  const parse = (s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
  };
  const x = parse(a);
  const y = parse(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return NaN;
  return Math.round((y - x) / 86400000);
}

/** Right-pad for the monospace card. The renderer honours `white-space: pre`. */
const pad = (s, n) => String(s).padEnd(n, ' ');

/** Left-pad, for a numeric column. */
const padStart = (s, n) => String(s).padStart(n, ' ');

const fixed = (v, places) => (typeof v === 'number' && Number.isFinite(v)
  ? v.toFixed(places) : '—');

const signed = (v, places) => (typeof v === 'number' && Number.isFinite(v)
  ? `${v >= 0 ? '+' : ''}${v.toFixed(places)}` : '—');

/** "13.9 years" reads; "5,082 days" does not, at this scale. Both, so neither is lost. */
function gapPhrase(targetDate, analogDate) {
  const days = daysBetween(analogDate, targetDate);
  if (!Number.isFinite(days)) return '';
  const years = Math.abs(days) / 365.2425;
  const when = days >= 0 ? 'before' : 'after';
  return `${Math.abs(days).toLocaleString()} days (${years.toFixed(1)} years) ${when} the target`;
}

/** The hover card for one analog. */
export function analogCard(match, rank, report, baseline) {
  const lines = [];
  // ⚠ THE TYPE LEADS. It is the one word a reader scanning a chart wants from a mark, and it is
  // the thing the badge abbreviates — a card that made them derive it from the family table
  // lower down would leave the three letters on the canvas unexplained.
  lines.push(typeLine(match, baseline, (report && report.matches || []).length));
  const gap = gapPhrase(report && report.targetDate, match.date);
  if (gap) lines.push(gap);
  lines.push(`Weighted ${fixed(match.weightedScore, 1)}  ·  raw ${fixed(match.rawBits, 1)} bits`);
  lines.push(`Matched ${match.matchedFactors} of ${match.applicableFactors} factors`
    + ` in ${match.factorGroups} famil${match.factorGroups === 1 ? 'y' : 'ies'}`);

  /*
   * ⚠ EACH LIST IS PRINTED IN THE UNIT IT IS SORTED BY, and the two differ.
   *
   * `AnalogScorer` sorts `standouts` by RAW bits — that list answers "which agreements would be
   * notable on their own", which is a rarity claim — and `hits` by WEIGHTED bits, because that
   * is the ranking key the match's own score came from.
   *
   * <p>Printing raw bits against the weighted ordering is what the first version did, and the
   * column came out 5.8 / 5.3 / 3.6 / 3.5 / 3.5 / 4.6: a list that reads as an unsorted bug and
   * is in fact correctly sorted by a number not on screen. Found by reading a live card.
   */
  const standouts = Array.isArray(match.standouts) ? match.standouts : [];
  const all = standouts.length ? standouts : (match.hits || []);
  const weighted = standouts.length === 0;
  const shown = all.slice(0, CARD_MAX_FACTORS);
  if (shown.length) {
    lines.push('');
    lines.push(weighted
      ? `${pad('STRONGEST FACTORS', 44)}${padStart('weighted bits', 14)}`
      : `${pad('STANDOUT FACTORS', 44)}${padStart('raw bits', 14)}`);
    shown.forEach((h) => {
      const value = weighted && typeof h.weightedBits === 'number' ? h.weightedBits : h.bits;
      const sep = typeof h.separationDeg === 'number' && Number.isFinite(h.separationDeg)
        ? `${fixed(h.separationDeg, 2)}° apart` : '';
      lines.push(`  ${pad(h.label || h.factor, 38)}${pad(sep, 12)}`
        + `${padStart(fixed(value, 1), 6)}`);
    });
    if (all.length > shown.length) {
      lines.push(`  …and ${all.length - shown.length} more`
        + ' — open the row in the panel for the full list');
    }
  }

  // THE FAMILY TOTALS ARE RAW BITS, and the heading says so. They answer a different question
  // from the list above — how much sheer improbability came from each kind of agreement — and
  // labelling the unit is cheaper than converting one column to match the other.
  const groups = Object.entries(hitsByGroup(match));
  if (groups.length) {
    lines.push('');
    lines.push(`${pad('FAMILIES THAT AGREED', 42)}${padStart('n', 6)}${padStart('raw bits', 10)}`);
    groups
      .sort((a, b) => (b[1].bits || 0) - (a[1].bits || 0))
      .forEach(([g, v]) => {
        lines.push(`  ${pad(FACTOR_LABELS[g] || g, 40)}${padStart(v.count, 6)}`
          + `${padStart(fixed(v.bits, 1), 10)}`);
      });
  }

  // WHAT HAPPENED NEXT, both halves. The raw return alone flatters an instrument that drifts up;
  // the edge alone hides how large the move was. The baseline is the instrument's own all-day
  // mean at the same horizon, which is what makes the second column mean anything.
  const fwd = match.forwardReturnPercent;
  const edge = match.outcomeEdge;
  if (fwd && Object.keys(fwd).length) {
    lines.push('');
    lines.push(`${pad(`${report && report.symbol ? report.symbol : 'The instrument'} `
      + 'AFTER THIS DAY', 38)}${padStart('return', 10)}${padStart('vs baseline', 14)}`);
    Object.keys(fwd).forEach((k) => {
      lines.push(`  ${pad(k, 36)}${padStart(`${signed(fwd[k], 2)}%`, 10)}`
        + `${padStart(signed(edge && edge[k], 2), 14)}`);
    });
  }
  return lines;
}

/**
 * Build the overlay.
 *
 * @param report the scanner's finished report
 * @param dates  the chart's own x-axis categories, `YYYY-MM-DD`, ascending
 * @returns `{overlay, bars, snapped, beyondChart, truncated, targetDrawn, collided}` —
 *          `overlay: null` when nothing lands on this chart, so the caller can tell "the scan
 *          found nothing" from "none of it is on screen". Those are different findings.
 */
export function buildPlanetaryAnalogOverlay(report, dates) {
  const matches = Array.isArray(report && report.matches) ? report.matches : [];
  const targetDate = report && report.targetDate;
  // ⚠ FROM EVERY MATCH IN THE REPORT, not only the ones that land on this chart. The panel and
  // the canvas must agree about a match's type, and an analog dropped for being older than the
  // price history is still one of the scan's own results.
  const baseline = baselineShares(matches);

  let snapped = 0;
  let beyondChart = 0;
  let beforeChart = 0;
  let collided = 0;
  // Insertion-ordered. The target goes in first so a bar carrying it keeps the target's own
  // shape however the analogs sort onto it — the same ordering the eclipse study uses for its
  // circles, and for the same reason.
  const byBar = new Map();

  const place = (day) => {
    if (!day) return null;
    /*
     * ⚠ BEFORE THE FIRST BAR IS DROPPED, and this was found by running it.
     *
     * `indexAtOrAfter` returns 0 for ANY date before the series starts, which is right for a
     * Saturday two days early and catastrophic for a date years early: it is the forward snap
     * meeting a gap it was never meant to cross. A 1985-2015 lookback on SPY — whose history
     * begins 1993-01-29 — piled SEVEN analogs onto bar 0, a cluster on the oldest candle that
     * nothing in the data supports.
     *
     * <p>The eclipse study has the same expression and cannot hit this, because its range IS
     * the chart's range. Here the lookback is independent of the loaded history, so a reader
     * asking for sixty years on a thirty-year chart meets it immediately.
     *
     * <p>The honest rule is the same one applied at the other end: a date outside the charted
     * span is not on this chart, and is counted rather than moved onto it.
     */
    if (dates.length > 0 && day < dates[0]) {
      beforeChart += 1;
      return null;
    }
    const idx = indexAtOrAfter(dates, day);
    if (idx === -1) {
      // Past the last bar — dropped, never clamped. Clamping piles every out-of-range date onto
      // the final column, which draws a cluster that is not there. The target legitimately lands
      // here whenever it is today and the market has not opened.
      beyondChart += 1;
      return null;
    }
    const barDate = dates[idx];
    if (barDate !== day) snapped += 1;
    let bucket = byBar.get(barDate);
    if (!bucket) {
      bucket = { date: barDate, target: null, analogs: [], snappedFrom: new Set() };
      byBar.set(barDate, bucket);
    }
    if (barDate !== day) bucket.snappedFrom.add(day);
    return bucket;
  };

  let targetDrawn = false;
  const targetBucket = place(targetDate);
  if (targetBucket) {
    targetDrawn = true;
    targetBucket.target = report;
  }

  // ⚠ CAPPED ON THE RANKING, before placement. `matches` is ordered strongest first, so the head
  // is the slice worth keeping; see the constant.
  const truncated = Math.max(0, matches.length - PLANETARY_ANALOG_MAX_MARKS);
  const kept = truncated > 0 ? matches.slice(0, PLANETARY_ANALOG_MAX_MARKS) : matches;

  kept.forEach((m, i) => {
    const bucket = place(m && m.date);
    if (!bucket) return;
    // The RANK is the table's own row number and must stay that even when a mark is dropped for
    // being off the chart — it is the key that ties the two views together.
    bucket.analogs.push({ match: m, rank: i + 1 });
    if (bucket.analogs.length > 1) collided += 1;
  });

  const buckets = Array.from(byBar.values()).filter((b) => b.target || b.analogs.length);
  if (buckets.length === 0) {
    return {
      overlay: null,
      bars: 0,
      snapped,
      beyondChart,
      beforeChart,
      truncated,
      targetDrawn,
      collided,
    };
  }

  // The pair indices that terminate at the target, for its own badge and card. Every pair ends
  // there — see the note on `B` below.
  const pairIndices = buckets.flatMap((b) => b.analogs.map((a) => a.rank)).sort((a, b) => a - b);
  const pairRange = pairIndices.length === 0 ? ''
    : (pairIndices.length === 1 ? `B${pairIndices[0]}`
      : `B${pairIndices[0]}–B${pairIndices[pairIndices.length - 1]}`);

  const points = buckets.map((bucket) => {
    const lines = [];
    let title;
    let symbol;
    let label;
    let badge;

    if (bucket.target) {
      /*
       * ⚠ EVERY PAIR ENDS ON THIS ONE BAR, and the badge says so rather than pretending.
       *
       * A pair is (analog, target): the analog is the FIRST event and the target is the SECOND,
       * connected one. A report has exactly one target, so `B1`, `B2` … `B17` are all the same
       * column — drawn as seventeen separate labels they would be one illegible stack claiming
       * seventeen bars where there is one.
       *
       * <p>So the index lives on the A side, where it distinguishes something, and the target
       * carries the RANGE. The pairing is still complete and traceable: A7's partner is this
       * bar, and there is nowhere else it could be.
       */
      title = `B — THE SECOND EVENT  ${targetDate}`;
      symbol = 'rect';
      label = 'B';
      badge = pairRange || 'B';
      lines.push(`The SECOND event of every pair: ${pairRange || 'no pairs'} all end here.`);
      lines.push('Each A-mark is the FIRST event of its pair — the historical day whose'
        + ' planetary signature resembles this one.');
      lines.push('');
      lines.push(`${report.factorsApplicable} factors applicable`
        + `${report.nonRecurringFactorCount
          ? ` · ${report.nonRecurringFactorCount} never recurred, so excluded` : ''}`);
      lines.push(`Scanned back to ${report.from} (${report.days} days)`);
    }

    bucket.analogs.forEach((entry, i) => {
      const type = matchType(entry.match, baseline);
      if (i > 0 || bucket.target) {
        lines.push('');
        lines.push(`A${entry.rank} → ${pairRange ? `B${entry.rank}` : 'B'}`
          + `   ${entry.match.date}`);
      }
      if (!title) {
        title = `A${entry.rank} → B${entry.rank}   ${entry.match.date}`;
        // A diamond means at least one factor would be notable on its own; a plain circle means
        // the score is an accumulation of ordinary agreements. SHAPE rather than colour, so it
        // survives a monochrome screenshot and a colour-blind reader — the same channel the dual
        // ledger uses for its solid and hollow silhouettes.
        symbol = (entry.match.standouts || []).length ? 'diamond' : 'circle';
        // INSIDE the symbol, the index alone. `A17` does not fit in an 18px mark at 9px, and
        // the badge above carries the full notation anyway — what this has to survive is
        // `hideOverlap` dropping the badge when the chart is zoomed out, which is exactly when
        // a reader is scanning the whole series.
        label = String(entry.rank);
        // THE BADGE IS THE NAME: pair notation and type in one readable chip. This is what makes
        // the chart scannable for a KIND of agreement rather than only for a rank.
        badge = type ? `A${entry.rank} ${type.code}` : `A${entry.rank}`;
      }
      lines.push(...analogCard(entry.match, entry.rank, report, baseline));
    });

    if (bucket.snappedFrom.size) {
      lines.push('');
      // Said out loud. A mark on a bar whose date is not the analog's date is the one thing here
      // a reader could reasonably take for a bug rather than a convention.
      lines.push(`Fell on ${Array.from(bucket.snappedFrom).sort().join(', ')}`
        + ' — a non-trading day, drawn on the next bar');
    }

    return {
      date: bucket.date,
      // NO price, deliberately — see the file note. This is what sends it to the rail.
      label,
      labelInside: true,
      symbol,
      badge,
      // THE SAME CARD from the badge as from the mark. Hovering the chip and hovering the
      // symbol are the same question about the same bar, and two different answers would be a
      // bug a reader could only find by trying both.
      badgeTooltipLines: lines,
      tooltipLines: lines,
      title,
      tooltipFooter: bucket.target
        ? 'Bits measure coincidence against this scan’s own history — not a forecast.'
        : 'Bits assume the factors are independent, which they are not, so the score is an '
          + 'upper bound. Read the price action, not the number.',
    };
  });

  return {
    overlay: {
      type: 'pivotMarkers',
      id: PLANETARY_ANALOG_OVERLAY_ID,
      points,
      anchor: 'bottom',
      color: PLANETARY_ANALOG_COLOR,
      colorRole: 'planetaryAnalog',
      label: 'Planetary analogs',
      // The fallback for a point that names no shape. Every one of them does.
      symbol: 'circle',
      // Large enough to hold two digits of rank. The eclipse study's 9px carries no label.
      symbolSize: 18,
      insideLabelFontSize: 9,
      // The badge is the pair notation and the type, so it is the thing being READ. 10 is the
      // renderer's own default and is what the CTS confluence letters use.
      badgeFontSize: 10,
      // ⚠ OPT-IN. The renderer draws every badge label at opacity 0 by inheritance from the
      // series that hides its 1px anchor symbol, and the default is kept so a study which has
      // never shown chips does not grow them. This one is BUILT around them.
      badgeVisible: true,
      // The faint full-height column that makes an 18px tick findable. See the file note.
      verticals: points.map((p) => ({ date: p.date })),
      verticalOpacity: VERTICAL_OPACITY,
      // ADMIN RESEARCH, so the short-bar preset does not swallow the hover card. That preset is a
      // customer-facing constraint — nothing astrological reaches the screen — and this study is
      // reachable only from a panel that renders nothing for a non-admin, so there is no customer
      // to protect. Without it the marks draw and answer no hover, which reads as broken rather
      // than as restricted. The whitelist in `chartDataProcessor` is the OTHER gate and a study
      // needs both; four have now shipped blind to that.
      researchTooltip: true,
      // ⚠ CONFINED, because this card hangs off a mark at the FOOT of the grid and there is no
      // room below it. Measured on a live chart: 880x522 at y=1074 in a 1271-high window, so
      // the forward-return table and the snapped-date note were 325px off the bottom of the
      // screen. The content was right and the placement was not.
      confineTooltip: true,
      source: 'planetaryAnalog',
    },
    bars: points.length,
    snapped,
    beyondChart,
    beforeChart,
    truncated,
    targetDrawn,
    collided,
  };
}

/** The one-line status. Every number it quotes is a decision, so none of them is silent. */
export function describePlanetaryAnalogPaint({
  matchCount, bars, snapped, beyondChart, beforeChart, truncated, targetDrawn, collided,
}) {
  if (!matchCount) return 'Planetary analogs: the scan returned none, so nothing was drawn.';
  if (!bars) {
    return `Planetary analogs: ${matchCount} found, but none of them lands on the loaded chart. `
      + 'Load a longer history, or shorten the lookback.';
  }
  const parts = [`Planetary analogs: ${bars} bar${bars === 1 ? '' : 's'} marked`];
  parts.push(targetDrawn ? 'target on the chart' : 'target is off the chart');
  if (snapped) parts.push(`${snapped} snapped forward from a non-trading day`);
  // ⚠ NAMED SEPARATELY from the other end, because the two have different cures: a lookback
  // reaching past the start of the price history is shortened, and an analog past the last bar
  // is simply in the future. Folding them into one count would hide which.
  if (beforeChart) {
    parts.push(`${beforeChart} older than the loaded history (shorten the lookback to see them)`);
  }
  if (beyondChart) parts.push(`${beyondChart} past the last bar`);
  if (collided) {
    parts.push(`${collided} share a bar with a stronger one (both are on that bar’s card)`);
  }
  if (truncated) {
    parts.push(`${truncated} weakest not drawn (cap ${PLANETARY_ANALOG_MAX_MARKS})`);
  }
  return `${parts.join(' · ')}.`;
}
